/**
 * ч.1107 (рішення власника): «Обліковий запис» → «Передача організації».
 * Організація ПЕРЕЇЖДЖАЄ в бізнес-акаунт іншого користувача з усім обліком;
 * довідники акаунта, якими вона користується, копіюються туди ж.
 *  • відправник (власник акаунта): обирає організацію, вказує пошту отримувача,
 *    підтверджує назвою, паролем і — для підтвердженої організації — ключем КЕП;
 *  • отримувач: бачить «Вам передають», обирає свій акаунт і приймає або відхиляє.
 * До прийняття нічого не змінюється; запит діє 7 днів.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountOrgs, orgTransfers, type AccountOrg, type OrgTransfer,
} from '@/api/accountOrgs';
import { useToast } from '@/components/ToastProvider';
import { useAuth } from '@/auth/store';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { extractApiError } from '@/utils/extractApiError';
import { RemovalsJournal } from './DeleteOrganizationSection';
import DestructiveConfirm from './DestructiveConfirm';

const STATUS: Record<OrgTransfer['status'], string> = {
  PENDING: 'очікує отримувача', ACCEPTED: 'прийнято', DECLINED: 'відхилено', CANCELLED: 'скасовано',
};
const dt = (v: string) => new Date(v).toLocaleString('uk-UA');
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Incoming() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['org-transfers-incoming'], queryFn: orgTransfers.incoming });
  const [target, setTarget] = useState<Record<string, string>>({});
  const [password, setPassword] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const accounts = q.data?.my_accounts ?? [];
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['org-transfers-incoming'] }),
    qc.invalidateQueries({ queryKey: ['orgs'] }), qc.invalidateQueries({ queryKey: ['account-orgs'] }),
    qc.invalidateQueries({ queryKey: ['orgs-for-modules'] }), qc.invalidateQueries({ queryKey: ['me'] }),
    qc.invalidateQueries({ queryKey: ['account-org-removals'] }),
  ]);
  if (!q.data?.transfers.length) return null;

  return (
    <div className="card" data-testid="transfers-incoming">
      <div className="toolbar"><h2 style={{ margin: 0, fontSize: '1.05rem' }}>Вам передають організації</h2></div>
      {q.data.transfers.map((t) => {
        const acc = target[t.id] ?? accounts[0]?.id ?? '';
        const pwd = password[t.id] ?? '';
        const lock = !accounts.length ? 'Прийняти можна лише у власний бізнес-акаунт — у вас такого ще немає'
          : !pwd ? 'Введіть пароль облікового запису' : undefined;
        return (
          <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.8rem',
            marginBottom: '0.6rem' }}>
            <div>
              <b>{t.organization_name}</b>{t.organization_code ? ` — ${t.organization_code}` : ''}
              <span style={{ color: 'var(--muted)' }}> · передає {t.from_user_name ?? '—'}
                {t.from_tenant_name ? ` («${t.from_tenant_name}»)` : ''} · діє до {dt(t.expires_at)}</span>
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.3rem 0 0.6rem' }}>
              Після прийняття організація з усіма документами, проводками і залишками переїде у ваш акаунт;
              контрагенти, номенклатура та інші довідники, якими вона користується, будуть скопійовані туди ж.
              У відправника організація зникне.
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="field" style={{ minWidth: 240 }}>
                <label>У який акаунт прийняти</label>
                <select value={acc} aria-label="Акаунт для прийняття"
                  onChange={(e) => setTarget({ ...target, [t.id]: e.target.value })}>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="field" style={{ width: 220 }}>
                <label>Пароль облікового запису</label>
                <input type="password" value={pwd} aria-label="Пароль для прийняття"
                  onChange={(e) => setPassword({ ...password, [t.id]: e.target.value })} />
              </div>
              <button type="button" className="primary" disabled={!!lock || busy !== null} title={lock}
                onClick={async () => {
                  setBusy(t.id);
                  try {
                    const res = await orgTransfers.accept(t.id, acc, pwd);
                    toast.show({ type: 'success', durationMs: 9000,
                      message: `Організацію «${t.organization_name}» прийнято. Скопійовано записів довідників: `
                        + `${res.copied_rows}; використано наявних: ${res.matched_rows}.` });
                    await refresh();
                  } catch (e) { toast.show({ type: 'error', message: extractApiError(e) }); } finally { setBusy(null); }
                }}>{busy === t.id ? 'переносимо…' : 'прийняти організацію'}</button>
              <button type="button" disabled={busy !== null}
                onClick={async () => {
                  if (!window.confirm(`Відхилити передачу організації «${t.organization_name}»?`)) return;
                  try { await orgTransfers.decline(t.id); await refresh(); }
                  catch (e) { toast.show({ type: 'error', message: extractApiError(e) }); }
                }}>відхилити</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Outgoing() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<AccountOrg | null>(null);
  const [email, setEmail] = useState('');
  const listQ = useQuery({ queryKey: ['account-orgs'], queryFn: accountOrgs.list });
  // ч.1406: перелік тепер по всіх власних акаунтах — передають лише з поточного.
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const ownRows = (listQ.data ?? []).filter((o) => !o.tenant_id || !activeTenantId || o.tenant_id === activeTenantId);
  const outQ = useQuery({ queryKey: ['account-transfers'], queryFn: accountOrgs.outgoing });
  const pending = new Set((outQ.data ?? []).filter((t) => t.status === 'PENDING').map((t) => t.organization_id));

  const orgCols = useMemo<ColumnDef<AccountOrg>[]>(() => [
    { key: 'name', header: 'Організація', sortValue: (o) => o.name, cell: (o) => o.name },
    { key: 'code', header: 'ЄДРПОУ / РНОКПП', width: 150, sortValue: (o) => o.code ?? '', cell: (o) => o.code ?? '' },
    { key: 'docs', header: 'Документів', width: 110, align: 'right', sortValue: (o) => o.documents,
      cell: (o) => o.documents.toLocaleString('uk-UA') },
    { key: 'state', header: 'Стан', width: 230, sortValue: (o) => Number(o.kep_confirmed),
      cell: (o) => (pending.has(o.id) ? 'очікує отримувача'
        : o.kep_confirmed ? 'підтверджена ключем КЕП' : 'не підтверджена') },
    { key: 'act', header: '', width: 110, align: 'center',
      cell: (o) => (
        <button type="button" className="btn-link" disabled={o.deletion_mark}
          title={o.deletion_mark ? 'Організацію позначено на видалення' : undefined}
          onClick={() => { setSelected(o); setEmail(''); }}>передати…</button>
      ) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [outQ.data]);

  const outCols = useMemo<ColumnDef<OrgTransfer>[]>(() => [
    { key: 'date', header: 'Дата', width: 150, sortValue: (t) => t.created_at, cell: (t) => dt(t.created_at) },
    { key: 'org', header: 'Організація', sortValue: (t) => t.organization_name, cell: (t) => t.organization_name },
    { key: 'to', header: 'Кому', width: 240, sortValue: (t) => t.to_email, cell: (t) => t.to_email },
    { key: 'status', header: 'Стан', width: 170, sortValue: (t) => t.status, cell: (t) => STATUS[t.status] },
    { key: 'act', header: '', width: 110, align: 'center',
      cell: (t) => (t.status === 'PENDING' ? (
        <button type="button" className="btn-link" onClick={async () => {
          try {
            await accountOrgs.cancelTransfer(t.id);
            await qc.invalidateQueries({ queryKey: ['account-transfers'] });
          } catch (e) { toast.show({ type: 'error', message: extractApiError(e) }); }
        }}>скасувати</button>
      ) : null) },
  ], [qc, toast]);

  const emailOk = EMAIL.test(email.trim());
  return (
    <>
      <div className="card">
        <div className="toolbar" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Передача організації</h2>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Передати організацію з усім її обліком у бізнес-акаунт іншого користувача.
            </div>
          </div>
        </div>
        <div style={{ background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '0.9rem', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
          Отримувач отримає запит і має прийняти його у свій акаунт протягом 7 днів. Після прийняття
          організація зникне з вашого акаунта разом з документами, проводками, працівниками і договорами.
          Контрагенти, номенклатура та інші довідники лишаються у вас — отримувачу їх буде скопійовано.
          Доступи ваших користувачів до цієї організації буде знято. Поки запит не прийнято, ви працюєте
          з організацією як звичайно і можете його скасувати.
        </div>
        <DataTable<AccountOrg> rows={ownRows} columns={orgCols} rowKey={(o) => o.id}
          loading={listQ.isLoading} emptyText="В акаунті немає організацій."
          rowBold={(o) => o.id === selected?.id} />
      </div>

      {selected && (
        <div className="card" data-testid="transfer-org-panel">
          <div className="toolbar">
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Передача: {selected.name}</h2>
            <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setSelected(null)}>скасувати</button>
          </div>
          <div className="field" style={{ maxWidth: 420, marginBottom: '0.8rem' }}>
            <label>Пошта отримувача (його обліковий запис в oblik.plus)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Пошта отримувача"
              placeholder="buhgalter@firma.ua" autoComplete="off" />
          </div>
          <DestructiveConfirm
            action="org-transfer" org={selected} kepRequired={selected.kep_confirmed}
            submitLabel="надіслати запит на передачу" busyLabel="надсилаємо…"
            disabled={!emailOk} disabledTitle="Вкажіть пошту отримувача"
            onSubmit={async (payload) => {
              await accountOrgs.transfer(selected.id, { ...payload, to_email: email.trim() });
              toast.show({ type: 'success', durationMs: 9000,
                message: `Запит на передачу «${selected.name}» надіслано: ${email.trim()}. Організація переїде після прийняття.` });
              setSelected(null);
              await qc.invalidateQueries({ queryKey: ['account-transfers'] });
            }}
          />
        </div>
      )}

      {(outQ.data?.length ?? 0) > 0 && (
        <div className="card">
          <div className="toolbar"><h2 style={{ margin: 0, fontSize: '1.05rem' }}>Ваші запити на передачу</h2></div>
          <DataTable<OrgTransfer> rows={outQ.data ?? []} columns={outCols} rowKey={(t) => t.id}
            initialSortKey="date" initialSortDir="desc" emptyText="—" />
        </div>
      )}
      <RemovalsJournal />
    </>
  );
}

export default function TransferOrganizationSection({ canSend }: { canSend: boolean }) {
  return (
    <>
      <Incoming />
      {canSend && <Outgoing />}
    </>
  );
}
